import React, { useEffect, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getCampaign } from '../../../../api/campaignApi';
import { Campaign } from '../../../../types/campaign';
import { format, parse } from 'date-fns';
import SkeletonCard from './SkeletonCard';
import styles from './ReportCard.module.scss';

interface CampaignInfoCardProps {
  campaignId: string;
  onDownloadReport?: () => void;
  onRefreshAll?: () => void;
}

export interface CampaignInfoCardRef {
  refresh: () => void;
}

const CampaignInfoCard = forwardRef<CampaignInfoCardRef, CampaignInfoCardProps>(
  ({ campaignId, onDownloadReport, onRefreshAll }, ref) => {
    const [campaign, setCampaign] = useState<Campaign | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchCampaign = useCallback(async () => {
      try {
        setLoading(true);
        setError(null);
        const campaignData = await getCampaign(parseInt(campaignId));
        setCampaign(campaignData);
      } catch (err) {
        console.error('Error fetching campaign:', err);
        setError('Erro ao carregar dados da campanha');
      } finally {
        setLoading(false);
      }
    }, [campaignId]);

    useEffect(() => {
      if (campaignId) {
        fetchCampaign();
      }
    }, [campaignId, fetchCampaign]);

    useImperativeHandle(ref, () => ({
      refresh: fetchCampaign
    }));

    if (loading) {
      return <SkeletonCard rows={2} columns={2} />;
    }

    if (error) {
      return (
        <Card className={styles.reportCard}>
          <CardContent>
            <div className="text-center text-destructive">
              <i className="ri-error-warning-line me-2"></i>
              {error}
            </div>
          </CardContent>
        </Card>
      );
    }

    if (!campaign) {
      return (
        <Card className={styles.reportCard}>
          <CardContent>
            <div className="text-center text-muted">
              <i className="ri-information-line me-2"></i>
              Campanha não encontrada
            </div>
          </CardContent>
        </Card>
      );
    }

    // Calculate date range from orders
    const getDateRange = () => {
      if (!campaign.orders || campaign.orders.length === 0) {
        return { startDate: null, endDate: null };
      }

      const parseLocalDate = (value: string | null | undefined) => {
        if (!value) return null;
        // If backend sends yyyy-MM-dd, parse as local to avoid timezone shifts
        if (value.length === 10 && value.includes('-')) {
          return parse(value, 'yyyy-MM-dd', new Date());
        }
        // Fallback for ISO strings with time
        return new Date(value);
      };

      const dates = campaign.orders
        .filter(order => order.startDate && order.endDate)
        .map(order => {
          const start = parseLocalDate(order.startDate);
          const end = parseLocalDate(order.endDate);
          if (!start || !end) return null;
          return { start, end };
        })
        .filter((d): d is { start: Date; end: Date } => !!d);

      if (dates.length === 0) {
        return { startDate: null, endDate: null };
      }

      const startDate = new Date(Math.min(...dates.map(d => d.start.getTime())));
      const endDate = new Date(Math.max(...dates.map(d => d.end.getTime())));

      return { startDate, endDate };
    };

    const { startDate, endDate } = getDateRange();

    // Function to determine campaign status and color
    const getCampaignStatus = () => {
      const now = new Date();

      if (!startDate || !endDate) {
        return { status: 'draft', color: 'warning', icon: 'ri-draft-line' };
      }

      const start = new Date(startDate);
      const end = new Date(endDate);

      if (now < start) {
        return { status: 'scheduled', color: 'warning', icon: 'ri-time-line' };
      } else if (now >= start && now <= end) {
        return { status: 'active', color: 'success', icon: 'ri-play-circle-line' };
      } else {
        return { status: 'completed', color: 'secondary', icon: 'ri-check-circle-line' };
      }
    };

    const campaignStatus = getCampaignStatus();

    return (
      <Card className={styles.reportCard}>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row items-center gap-4">
            <div className="w-full lg:w-2/3 flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <div className={`bg-${campaignStatus.color} bg-opacity-10 rounded-full p-2`}>
                    <i className={`ri-${campaignStatus.icon} text-${campaignStatus.color} text-2xl`}></i>
                  </div>
                </div>
                <div className="flex-grow-1">
                  <h5 className="mb-1 font-semibold text-foreground">{campaign.name}</h5>
                  <div className="flex items-center gap-2">
                    <span className={`badge bg-${campaignStatus.color} bg-opacity-10 text-${campaignStatus.color} px-2 py-1 rounded-full text-xs`}>
                      <i className={`ri-${campaignStatus.icon} mr-1`}></i>
                      {campaignStatus.status === 'draft' && 'Rascunho'}
                      {campaignStatus.status === 'scheduled' && 'Agendada'}
                      {campaignStatus.status === 'active' && 'Ativa'}
                      {campaignStatus.status === 'completed' && 'Finalizada'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-center">
                  <i className="ri-building-line text-muted mr-2"></i>
                  <div>
                    <small className="text-muted block">Agência</small>
                    <span className="font-medium">{campaign.agency?.name || 'N/A'}</span>
                  </div>
                </div>
                <div className="flex items-center">
                  <i className="ri-user-line text-muted mr-2"></i>
                  <div>
                    <small className="text-muted block">Anunciante</small>
                    <span className="font-medium">{campaign.advertiser?.name || 'N/A'}</span>
                  </div>
                </div>
                <div className="col-span-1 md:col-span-2 flex items-center">
                  <i className="ri-calendar-line text-muted mr-2"></i>
                  <div>
                    <small className="text-muted block">Data de veiculação</small>
                    <span className="font-medium">
                      {startDate && endDate
                        ? `${format(startDate, 'dd/MM/yyyy')} à ${format(endDate, 'dd/MM/yyyy')}`
                        : 'N/A'
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full lg:w-1/3 flex flex-col gap-2 justify-center">
              <Button
                variant="outline"
                onClick={onRefreshAll}
                className="w-full py-2"
                title="Atualizar todos os relatórios"
              >
                <i className="ri-refresh-line mr-2"></i>
                Atualizar tudo
              </Button>
              <Button
                onClick={onDownloadReport}
                className="w-full py-2"
              >
                <i className="ri-download-line mr-2"></i>
                Baixar relatório
              </Button>
              {campaign.confirmationReportUrl && (
                <a
                  href={campaign.confirmationReportUrl}
                  download
                  className="btn btn-success w-full py-2"
                >
                  <i className="ri-file-zip-line mr-2"></i>
                  Baixar relatórios de conclusão
                </a>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
);

CampaignInfoCard.displayName = 'CampaignInfoCard';

export default CampaignInfoCard;
